import _ from 'lodash'
import React from 'react'

import {
  applyTagTransform,
  getOptionSource,
  getReferenceListKey,
  resolveEntityTypeId,
  useAvailableReferenceListKeys,
  useDistinctFieldValues,
  useReferenceListFirmId,
  useReferenceListOptions,
} from 'browser/components/atomic-elements/molecules/fields/reference-list-options'

/**
 * HOC that resolves a select field's options from its `optionSource` and
 * normalizes creatable values via a `transform` formula. Precedence: firm
 * reference list → distinct existing values → static enum/suggestions. No-ops
 * (renders untouched) when the field declares neither optionSource nor transform,
 * so it is safe on the shared select.
 *
 * `referenceListKeys` is the authoring counterpart: instead of a list's values it
 * offers the keys the firm could still create a list for, derived from the fields
 * that declared `referenceList`. It replaces the options outright and sits
 * outside the precedence chain above.
 */
export function withOptionSource<
  P extends {
    entity?: any
    entityType?: string
    frames?: any
    options?: any[]
    isLoading?: boolean
    onChange?: (values: any, selectedOptions?: any) => void
    transform?: string
    suggestionsSize?: number
  }
>(Wrapped: React.ComponentType<P>): React.FC<P> {
  const WithOptionSource: React.FC<P> = (props) => {
    const optionSource = getOptionSource(props)
    const referenceListKey = getReferenceListKey(props)
    const wantsDistinct = !!optionSource?.distinctValues
    const wantsKeys = !!optionSource?.referenceListKeys
    const { transform } = props

    // Hold the list load while an edge-based firm is still resolving, so we don't
    // fetch against the wrong firm (see referenceListFirmEdge).
    const { firmId, resolving: isResolvingFirm } = useReferenceListFirmId(props.entity, optionSource)
    const valuePath: string[] = props.frames?.getContext?.('valuePath') ?? []

    const { options: keyOptions, isLoading: isLoadingKeys } = useAvailableReferenceListKeys(
      wantsKeys && !isResolvingFirm,
      firmId
    )

    const { options: referenceOptions, isLoading: isLoadingReference, loaded: referenceLoaded } =
      useReferenceListOptions(isResolvingFirm ? undefined : referenceListKey, firmId)

    // Only fetch distinct values when enabled AND the reference list produced
    // nothing (it takes precedence); undefined entityType skips the load.
    const distinctEntityType =
      wantsDistinct && !isResolvingFirm && referenceLoaded && _.isEmpty(referenceOptions)
        ? resolveEntityTypeId(props.entity, props.entityType, valuePath)
        : undefined
    const { options: distinctOptions, isLoading: isLoadingDistinct } = useDistinctFieldValues(
      distinctEntityType,
      valuePath,
      props.suggestionsSize,
      firmId
    )

    if (!referenceListKey && !wantsDistinct && !wantsKeys && !transform) {
      return <Wrapped {...props} />
    }

    // Strict precedence, not a union: a firm reference list fully replaces the
    // static suggestions. Only when none resolves do we fall back to distinct
    // existing values + the field's static enum/suggestions. `referenceListKeys`
    // is the authoring source and replaces the options outright.
    const options = wantsKeys
      ? keyOptions
      : _.isEmpty(referenceOptions)
        ? _.uniqBy([...distinctOptions, ...(props.options ?? [])], 'value')
        : _.uniqBy(referenceOptions, 'value')
    const onChange = transform
      ? (values: any, selectedOptions?: any) => props.onChange?.(applyTagTransform(transform, values), selectedOptions)
      : props.onChange

    return (
      <Wrapped
        {...props}
        options={options}
        onChange={onChange}
        isLoading={
          props.isLoading || isResolvingFirm || isLoadingReference || isLoadingDistinct || isLoadingKeys
        }
      />
    )
  }
  WithOptionSource.displayName = `withOptionSource(${Wrapped.displayName || Wrapped.name || 'Component'})`
  return WithOptionSource
}
